/**
 * Team Member Detail Hook
 *
 * Loads the data behind the Employment Details and Position Details tabs of the
 * TeamMemberDetailModal for a single team member.
 *
 * Field sourcing mirrors PersonalInformation.jsx so the manager view shows the
 * exact same data as the employee-facing profile:
 *   - Employment fields (Summary + Job Details): /data/EmployeesV2
 *   - Position rows: /data/PositionsV2 (WorkerPersonnelNumber filter)
 *
 * Always refetches on mount (staleTime: 0) rather than serving a cached
 * value, so fields updated by HR (e.g. DGMA_EmploymentProbationPeriod,
 * DGMA_ExtendedProbationDate) show up immediately instead of only after
 * the previous cache entry expires.
 *
 * Returns:
 *   - employment: raw EmployeesV2 record (or null)
 *   - positions: Array of PositionsV2 records
 *   - isLoading: boolean
 *   - error: Error | null
 */
import { useQuery } from "react-query";
import { employeeService, positionService } from "../../api/services";

export default function useTeamMemberDetail(personnelNumber) {
  const {
    data,
    isLoading,
    error,
  } = useQuery(
    ["teamMemberDetail", personnelNumber],
    async () => {
      console.log("📋 Fetching detail for team member:", personnelNumber);

      // Fetch employment record and position assignments in parallel.
      const [employmentRes, positionsRes] = await Promise.all([
        employeeService.getEmployeeByNumber(personnelNumber),
        positionService.getPositionsByWorker(personnelNumber),
      ]);

      const employment = employmentRes.data?.value?.[0] || null;
      const positions = positionsRes.data?.value || [];

      return { employment, positions };
    },
    {
      enabled: !!personnelNumber,
      retry: 2,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );

  return {
    employment: data?.employment ?? null,
    positions: data?.positions ?? [],
    isLoading: !!personnelNumber && isLoading,
    error: error || null,
  };
}
